// src/components/PartOrderModal.jsx
import React, {useEffect, useMemo, useRef, useState} from "react";
import Forms from "./Forms";
import Error from "./Error";
import {useTranslation} from "react-i18next";
import API from "../utils/API";
import {API_ENDPOINTS} from "../apiConfig";

const CURRENCY_OPTIONS = [
    {value: "CZK", label: "CZK"},
    {value: "EUR", label: "EUR"},
    {value: "USD", label: "USD"},
];

const DISCOUNT_TYPES = [
    {value: "percentage", label: "percentage"},
    {value: "fixed", label: "fixed"},
];

const toNumber = (v) => (v === "" || v === null || v === undefined ? "" : Number(v));
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

function PartRow({idx, row, onChange, partOptions, t, disablePartSelect = false}) {
    // priceSource: které total pole bylo naposledy změněno uživatelem
    const [priceSource, setPriceSource] = useState("net"); // 'net' | 'gross'

    const setPatch = (patch) => onChange(idx, {...row, ...patch});
    const setTotal = (patch) => setPatch({totalPrice: {...(row.totalPrice || {}), ...patch}});

    // Per-unit výpočet = total / quantity (ochrana proti 0)
    const computePerUnit = (totalNet, totalGross, qty) => {
        const q = Number(qty) || 0;
        if (!q || totalNet === "" || totalGross === "") return {net: "", gross: ""};
        return {
            net: round2(Number(totalNet) / q),
            gross: round2(Number(totalGross) / q),
        };
    };

    const onChangeNetTotal = (v) => {
        const net = toNumber(v);
        const rate = Number(row.totalPrice?.vatRate) || 0;
        const gross = net === "" ? "" : round2(Number(net) * (1 + rate / 100));
        setTotal({net, gross});
        setPriceSource("net");
    };

    const onChangeGrossTotal = (v) => {
        const gross = toNumber(v);
        const rate = Number(row.totalPrice?.vatRate) || 0;
        const divisor = 1 + rate / 100;
        const net = gross === "" ? "" : round2(Number(gross) / divisor);
        setTotal({gross, net});
        setPriceSource("gross");
    };

    const onChangeVat = (v) => {
        const vatRate = toNumber(v);
        const net = row.totalPrice?.net;
        const gross = row.totalPrice?.gross;
        if (priceSource === "net") {
            const newGross = net === "" ? "" : round2(Number(net) * (1 + vatRate / 100));
            setTotal({vatRate, gross: newGross});
        } else {
            const divisor = 1 + (Number(vatRate) / 100);
            const newNet = gross === "" ? "" : round2(Number(gross) / divisor);
            setTotal({vatRate, net: newNet});
        }
    };

    // dopočítané ceny za kus (z total)
    const perUnit = computePerUnit(row.totalPrice?.net, row.totalPrice?.gross, row.quantity);

    return (
        <div className="border rounded p-2 space-y-2">
            {/* Řádek 1: Díl + Quantity */}
            <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-6">
                    <Forms.Select
                        label={t("part")}
                        value={row.part ? partOptions.find((p) => p.value === row.part) : null}
                        options={partOptions}
                        onChange={(opt) => setPatch({part: opt?.value || null})}
                        isClearable
                        disabled={disablePartSelect}
                    />
                </div>
                <div className="col-span-3">
                    <Forms.Number
                        min={0}
                        step="1"
                        label={t("quantity")}
                        value={row.quantity}
                        onChange={val => setPatch({quantity: toNumber(val)})}
                    />
                </div>
                <div className="col-span-3">
                    <Forms.Text
                        label={t("orderId")}
                        value={row.orderId}
                        onChange={val => setPatch({orderId: val})}
                    />
                </div>
            </div>

            {/* Řádek 2: Měna, DPH, Total NET ↔ Total GROSS */}
            <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                    <Forms.Select
                        label={t("currency")}
                        value={CURRENCY_OPTIONS.find((o) => o.value === (row.totalPrice?.currency || "CZK"))}
                        options={CURRENCY_OPTIONS}
                        onChange={(opt) => setTotal({currency: opt?.value || "CZK"})}
                    />
                </div>
                <div className="col-span-2">
                    <Forms.Number
                        min={0}
                        label={t("vatRate")}
                        placeholder="21"
                        value={row.totalPrice?.vatRate ?? 21}
                        onChange={onChangeVat}
                    />
                </div>
                <div className="col-span-3">
                    <Forms.Number
                        min={0}
                        label={t("net")}      // CELKEM bez DPH
                        placeholder="0.00"
                        value={row.totalPrice?.net ?? ""}
                        onChange={onChangeNetTotal}
                    />
                </div>
                <div className="col-span-4">
                    <Forms.Number
                        min={0}
                        label={t("gross")}    // CELKEM s DPH
                        placeholder="0.00"
                        value={row.totalPrice?.gross ?? ""}
                        onChange={onChangeGrossTotal}
                    />
                </div>
            </div>

            {/* Náhled: cena za kus (read-only) */}
            <div className="grid grid-cols-12 gap-2 items-center text-sm text-gray-600">
                <div className="col-span-12">
                    {t("pricePerUnit")}:{" "}
                    {perUnit.net === "" || perUnit.gross === "" ? "—" : (
                        <>
                            {t("net")}: {perUnit.net} • {t("gross")}: {perUnit.gross}{" "}
                            ({row.totalPrice?.currency || "CZK"})
                        </>
                    )}
                </div>
            </div>

            {/* Odstranění položky */}
            <div className="flex justify-end">
                <Forms.Button variant="text" label={t("remove")} onClick={() => onChange(idx, null)}/>
            </div>
        </div>
    );
}

const PartOrderModal = ({close, onOrderSaved, order}) => {
    const {t} = useTranslation();
    const inputRef = useRef(null);
    const isEdit = !!order?._id;

    const [parts, setParts] = useState([]);
    const [error, setError] = useState();

    const [form, setForm] = useState({
        label: "",
        description: "",
        orderDate: new Date(),
        finishedDate: "",
        discount: {type: null, value: ""},
        partList: [], // { part, orderId, quantity, totalPrice{currency,net,gross,vatRate}, pricePerUnit (computed), delivered? }
    });

    // Načtení dílů
    useEffect(() => {
        (async () => {
            try {
                const {data} = await API.get(API_ENDPOINTS.partSku.list);
                setParts(
                    (data || []).map((p) => ({
                        value: p._id,
                        label: `${p.name} - ${(p?.part?.compatibleWith || []).map(model => model.name).join(", ")}`,
                    }))
                );
            } catch (e) {
                setError(e);
            }
        })();
    }, []);

    // Předvyplnění při editaci
    useEffect(() => {
        if (!order) return;
        setForm({
            label: order.label || "",
            description: order.description || "",
            orderDate: order.orderDate ? new Date(order.orderDate) : "",
            finishedDate: order.finishedDate ? new Date(order.finishedDate) : "",
            discount: {
                type: order.discount?.type || null,
                value: (order.discount?.value === 0 || order.discount?.value) ? order.discount.value : "",
            },
            partList: (order.partList || []).map((it) => ({
                part: (typeof it.part === "object") ? it.part._id : it.part || null,
                orderId: it.orderId,
                quantity: (it.quantity === 0 || it.quantity) ? Number(it.quantity) : 1,
                totalPrice: {
                    currency: it.totalPrice?.currency || "CZK",
                    net: (it.totalPrice?.net === 0 || it.totalPrice?.net) ? Number(it.totalPrice.net) : "",
                    gross: (it.totalPrice?.gross === 0 || it.totalPrice?.gross) ? Number(it.totalPrice.gross) : "",
                    vatRate: (it.totalPrice?.vatRate === 0 || it.totalPrice?.vatRate) ? Number(it.totalPrice.vatRate) : 21,
                },
                // pricePerUnit se dopočítá při odeslání/renderu
                delivered: !!it.delivered,
                _id: it._id,
            })),
        });
    }, [order]);

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 1);
    }, []);

    const selectedPartIds = useMemo(() => {
        const s = new Set();
        for (const it of form.partList || []) {
            if (it?.part) s.add(it.part);
        }
        return s;
    }, [form.partList]);

    const getOptionsForRow = (row) => {
        return parts.filter(opt => opt.value === row.part || !selectedPartIds.has(opt.value));
    };

    const allPartsPicked = parts.length > 0 && selectedPartIds.size >= parts.length;

    const setField = (name, value) => setForm((prev) => ({...prev, [name]: value}));

    const onRowChange = (idx, updated) => {
        setForm((prev) => {
            const next = [...prev.partList];
            if (updated === null) next.splice(idx, 1);
            else next[idx] = updated;
            return {...prev, partList: next};
        });
    };

    const addRow = () => {
        setForm((prev) => ({
            ...prev,
            partList: [
                ...(prev.partList || []),
                {
                    part: null,
                    quantity: 1,
                    totalPrice: {currency: "CZK", net: "", gross: "", vatRate: 21},
                    delivered: false,
                },
            ],
        }));
    };

    // Součty (z totalPrice.gross)
    const totals = useMemo(() => {
        const map = new Map();
        for (const it of form.partList || []) {
            const curr = it.totalPrice?.currency || "CZK";
            const gross = Number(it.totalPrice?.gross) || 0;
            map.set(curr, (map.get(curr) || 0) + gross);
        }
        // sleva na GROSS
        if (form.discount?.type && form.discount?.value) {
            if (form.discount.type === "percentage") {
                const pct = Number(form.discount.value) || 0;
                for (const [curr, amt] of map) map.set(curr, amt * (1 - pct / 100));
            } else if (form.discount.type === "fixed" && map.size === 1) {
                const [[curr, amt]] = Array.from(map.entries());
                map.set(curr, Math.max(0, amt - (Number(form.discount.value) || 0)));
            }
        }
        return map;
    }, [form.partList, form.discount]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(undefined);

        try {
            // vyrob pricePerUnit (z total / quantity)
            const mappedPartList = (form.partList || [])
                .filter((it) => it.part && it.quantity)
                .map((it) => {
                    const q = Number(it.quantity) || 0;
                    const curr = it.totalPrice?.currency || "CZK";
                    const netTotal = Number(it.totalPrice?.net) || 0;
                    const grossTotal = Number(it.totalPrice?.gross) || 0;
                    const vatRate = Number(it.totalPrice?.vatRate) || 0;

                    const unit = q > 0 ? {
                        currency: curr,
                        net: round2(netTotal / q),
                        gross: round2(grossTotal / q),
                        vatRate
                    } : {currency: curr, net: 0, gross: 0, vatRate};

                    return {
                        part: it.part,
                        orderId: it.orderId,
                        quantity: q,
                        totalPrice: {currency: curr, net: netTotal, gross: grossTotal, vatRate},
                        pricePerUnit: unit,
                        delivered: !!it.delivered,
                    };
                });

            const payload = {
                label: form.label.trim(),
                description: form.description,
                orderDate: form.orderDate || null,
                finishedDate: form.finishedDate || null,
                discount: form.discount?.type
                    ? {type: form.discount.type, value: Number(form.discount.value) || 0}
                    : undefined,
                partList: mappedPartList,
            };

            if (isEdit) await API.put(API_ENDPOINTS.partOrders.edit(order._id), payload);
            else await API.post(API_ENDPOINTS.partOrders.add, payload);

            await onOrderSaved?.();
            close();
        } catch (e) {
            setError(e);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Text ref={inputRef} required label={t("label")} value={form.label}
                            onChange={(v) => setField("label", v)}/>
                <Forms.Text label={t("description")} value={form.description}
                            onChange={(v) => setField("description", v)}/>
                <Forms.Date label={t("orderDate")} value={form.orderDate}
                            onChange={(v) => setField("orderDate", v)} required/>
                <Forms.Date label={t("finishedDate")} value={form.finishedDate}
                            onChange={(v) => setField("finishedDate", v)}/>
            </div>

            <div className="mb-3">
                <div className="font-medium mb-2">{t("items")}</div>
                <div className="flex justify-end mb-2">
                    <Forms.Button label={t("addItem")} onClick={addRow} disabled={allPartsPicked}/>
                </div>

                <div className="space-y-3">
                    {(form.partList || []).length === 0 && (
                        <div className="text-sm text-gray-500">{t("noItems")}</div>
                    )}
                    {(form.partList || []).map((row, idx) => (
                        <PartRow
                            key={row._id || idx}
                            idx={idx}
                            row={row}
                            onChange={onRowChange}
                            partOptions={getOptionsForRow(row)}
                            t={t}
                        />
                    ))}
                </div>
            </div>

            <div>
                {t('discount')}
                <div className="grid grid-cols-3 gap-4 mb-4 items-end">
                    <Forms.Select
                        label={t("discountType")}
                        value={form.discount?.type ? DISCOUNT_TYPES.find((o) => o.value === form.discount.type) : null}
                        options={DISCOUNT_TYPES}
                        onChange={(opt) => setField("discount", {
                            type: opt?.value || null,
                            value: (form.discount?.value === 0 || form.discount?.value) ? form.discount.value : "",
                        })}
                        isClearable
                    />
                    <Forms.Number
                        min={0}
                        label={t("discountValue")}
                        value={form.discount?.value ?? ""}
                        onChange={(v) => setField("discount", {
                            type: form.discount?.type || null,
                            value: v === "" ? "" : Number(v),
                        })}
                    />
                    <div>
                        <div className="text-sm text-gray-600 mb-1">{t("total")}</div>
                        <div className="text-l">
                            {Array.from(totals.entries()).map(([curr, amt]) => (
                                <span key={curr} className="mr-3">
                {new Intl.NumberFormat("cs-CZ", {style: "currency", currency: curr}).format(amt)}
              </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {error && <Error error={error}/>}
            <Forms.Button submit label={isEdit ? t("update") : t("save")} className="mt-4"/>
        </form>
    );
};

export default PartOrderModal;
